<template>
  <div class="account-approval-table-container">
    <div class="d-flex justify-space-between align-center p-4 bg-white border-b">
      <div class="d-flex align-center">
        <span class="text-lg font-semibold mr-4">Account & Journal Approvals</span>
        <v-btn-toggle
          v-model="filters.entityType"
          mandatory
          dense
          color="primary"
          class="mr-4"
          @change="handleFilterChange"
        >
          <v-btn value="" small class="text-none">All</v-btn>
          <v-btn value="ACCOUNT" small class="text-none">Accounts</v-btn>
          <v-btn value="JOURNAL" small class="text-none">Journals</v-btn>
        </v-btn-toggle>
      </div>

      <div class="d-flex align-center">
        <v-select
          v-model="filters.status"
          label="Select Status"
          :items="statusOptions"
          solo
          flat
          clearable
          outlined
          dense
          hide-details
          class="mr-2"
          style="width: 180px"
          @change="handleFilterChange"
        ></v-select>
        <v-btn color="primary" icon @click="handleFilterChange" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="approvals"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalItems"
      class="table-radius bg-white"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
    >
      <!-- Info Action column -->
      <template v-slot:[`item.viewDetails`]="{ item }">
        <v-btn icon small color="primary" @click="openInfoDialog(item)">
          <v-icon small>mdi-information-outline</v-icon>
        </v-btn>
      </template>

      <!-- Entity & Reference column -->
      <template v-slot:[`item.entityDetails`]="{ item }">
        <div class="p-2" style="min-width: 350px">
          <div class="d-flex align-center">
            <v-chip
              x-small
              label
              class="font-semibold mr-2 flex-shrink-0"
              :color="
                item.entityType === 'ACCOUNT'
                  ? 'teal lighten-5 text-teal-800'
                  : 'purple lighten-5 text-purple-800'
              "
            >
              {{ item.entityType }}
            </v-chip>
            <span class="font-semibold text-blue-600">
              {{ getEntityIdentifier(item) }}
            </span>
          </div>
          <span class="text-xs text-gray-500 mt-1 d-block">
            {{ getEntitySecondaryInfo(item) }}
          </span>
        </div>
      </template>

      <!-- Requested By column -->
      <template v-slot:[`item.requestedBy`]="{ item }">
        <div class="p-2">
          <p class="font-semibold mb-0">
            {{ getRequesterName(item) }}
          </p>
          <span class="text-xs text-gray-500">
            {{ formatDate(item.requestedOn) }}
          </span>
        </div>
      </template>

      <!-- Approval Type column -->
      <template v-slot:[`item.approvalType`]="{ item }">
        <v-chip small label class="font-semibold" :color="getApprovalTypeColor(item.approvalType)">
          {{ formatApprovalType(item.approvalType) }}
        </v-chip>
      </template>

      <!-- Remarks / Reason column -->
      <template v-slot:[`item.remarks`]="{ item }">
        <div class="max-w-xs truncate">
          <span>{{ item.remarks || item.payload?.remarks || '-' }}</span>
        </div>
      </template>

      <!-- Status column -->
      <template v-slot:[`item.status`]="{ item }">
        <v-chip small label class="font-semibold" :color="getStatusColor(item.status)">
          {{ item.status }}
        </v-chip>
      </template>

      <!-- Action column -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center py-2" v-if="item.status === 'PENDING'">
          <v-btn
            small
            color="success"
            class="mr-2 text-none"
            depressed
            @click="openActionDialog(item, 'APPROVE')"
          >
            <v-icon small left>mdi-check</v-icon>
            Approve
          </v-btn>
          <v-btn
            small
            color="error"
            class="text-none"
            outlined
            @click="openActionDialog(item, 'REJECT')"
          >
            <v-icon small left>mdi-close</v-icon>
            Reject
          </v-btn>
        </div>

        <div v-else class="text-xs text-gray-600">
          <span v-if="item.status === 'APPROVED'" class="text-green-600 font-semibold">
            ✓ Approved
          </span>
          <span v-else-if="item.status === 'REJECTED'" class="text-red-600 font-semibold">
            ✕ Rejected: {{ getRejectionReason(item) }}
          </span>
          <span v-else>{{ item.status }}</span>
        </div>
      </template>
    </v-data-table>

    <!-- Confirmation / Remarks Dialog -->
    <v-dialog v-model="dialog.show" max-width="500" persistent>
      <v-card>
        <v-card-title
          class="p-2 text-h6 font-weight-bold d-flex justify-space-between align-center"
        >
          <span>{{ dialog.actionType === 'APPROVE' ? 'Approve Request' : 'Reject Request' }}</span>
          <v-btn icon small @click="dialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="p-4">
          <p class="text-sm text-gray-700">
            Are you sure you want to <strong>{{ dialog.actionType.toLowerCase() }}</strong> this
            {{ dialog.item?.entityType }} request for
            <strong>{{ getEntityIdentifier(dialog.item) }}</strong
            >?
          </p>

          <v-textarea
            v-if="dialog.actionType === 'REJECT'"
            v-model="dialog.rejectionReason"
            label="Rejection Reason *"
            outlined
            dense
            rows="2"
            class="mt-2"
            placeholder="Please specify the reason for rejection..."
            :rules="[(v) => !!v || 'Rejection reason is required']"
          ></v-textarea>

          <v-textarea
            v-model="dialog.remarks"
            label="Reviewer Remarks (Optional)"
            outlined
            dense
            rows="2"
            class="mt-2"
            placeholder="Add any internal remarks..."
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="pt-0 pb-4 px-6 d-flex justify-end">
          <v-btn text class="text-none" @click="dialog.show = false">Cancel</v-btn>
          <v-btn
            :color="dialog.actionType === 'APPROVE' ? 'success' : 'error'"
            class="text-none px-4"
            depressed
            :loading="dialog.submitting"
            :disabled="dialog.actionType === 'REJECT' && !dialog.rejectionReason"
            @click="submitAction"
          >
            Confirm {{ dialog.actionType === 'APPROVE' ? 'Approval' : 'Rejection' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details / Diff Dialog (Audit Comparison View) -->
    <v-dialog v-model="infoDialog.show" max-width="70vw" scrollable>
      <v-card class="rounded-lg overflow-hidden border bg-white" v-if="infoDialog.item">
        <!-- Header -->
        <div class="pa-4 bg-slate-50 border-b d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-compare</v-icon>
            <span
              class="text-subtitle-1 font-weight-black grey--text text--darken-4 uppercase tracking-widest"
            >
              {{ getApprovalDialogTitle(infoDialog.item) }}
            </span>
          </div>
          <v-btn icon @click="infoDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Scrollable Content -->
        <v-card-text class="pa-6" style="max-height: 70vh">
          <!-- Metadata Summary Bar -->
          <div class="row no-gutters bg-slate-50 border rounded-lg pa-4 mb-6">
            <div class="col-12 col-md-3 d-flex flex-column px-2">
              <span class="text-[10px] font-weight-bold grey--text uppercase tracking-wider"
                >Action</span
              >
              <v-chip
                small
                label
                class="font-weight-black uppercase text-[10px] mt-1 w-max"
                :color="getApprovalActionColor(infoDialog.item.approvalType)"
                dark
              >
                {{ getApprovalActionText(infoDialog.item.approvalType) }}
              </v-chip>
            </div>
            <div class="col-12 col-md-3 d-flex flex-column px-2">
              <span class="text-[10px] font-weight-bold grey--text uppercase tracking-wider"
                >Posting Type</span
              >
              <span class="text-sm font-weight-bold grey--text text--darken-4 mt-1">
                {{
                  infoDialog.item.payload?.autoPosting || infoDialog.item.snapshot?.autoPosting
                    ? 'Auto Posting'
                    : 'Manual Posting'
                }}
              </span>
            </div>
            <div class="col-12 col-md-3 d-flex flex-column px-2">
              <span class="text-[10px] font-weight-bold grey--text uppercase tracking-wider"
                >Created At</span
              >
              <span class="text-sm font-weight-bold grey--text text--darken-4 mt-1">
                {{ formatAuditDateTime(infoDialog.item.requestedOn || infoDialog.item.createdAt) }}
              </span>
            </div>
            <div class="col-12 col-md-3 d-flex flex-column px-2">
              <span class="text-[10px] font-weight-bold grey--text uppercase tracking-wider"
                >Performed By</span
              >
              <span class="text-sm font-weight-bold grey--text text--darken-4 mt-1">
                {{ getRequesterName(infoDialog.item) }}
              </span>
            </div>
          </div>

          <!-- COMPARISON FOR JOURNAL -->
          <div v-if="infoDialog.item.entityType === 'JOURNAL'">
            <h4
              class="text-subtitle-2 font-weight-black grey--text text--darken-4 uppercase tracking-wider mb-3 px-2"
            >
              Comparison (Before vs After)
            </h4>
            <div class="border rounded overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-slate-50 border-b text-center font-weight-bold">
                  <tr>
                    <th class="pa-3 text-left border-r" rowspan="2">Account</th>
                    <th class="pa-3 border-b border-r" colspan="2">Before Update</th>
                    <th class="pa-3 border-b" colspan="2">After Update</th>
                  </tr>
                  <tr class="bg-slate-50/30 text-xs">
                    <th class="pa-2 text-right border-r" width="15%">Debit</th>
                    <th class="pa-2 text-right border-r" width="15%">Credit</th>
                    <th class="pa-2 text-right border-r" width="15%">Debit</th>
                    <th class="pa-2 text-right" width="15%">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(diff, dIdx) in calculateApprovalComparison(infoDialog.item)"
                    :key="dIdx"
                    class="border-b last:border-0"
                  >
                    <td class="pa-3 border-r">
                      <div class="d-flex flex-column">
                        <span class="font-weight-bold">{{ diff.particulars }}</span>
                        <span v-if="diff.descChanged" class="text-[10px] blue--text">
                          Description: "{{ diff.descBefore }}" &rarr; "{{ diff.descAfter }}"
                        </span>
                        <span v-else-if="diff.descAfter" class="text-[10px] grey--text">
                          {{ diff.descAfter }}
                        </span>
                      </div>
                    </td>
                    <!-- Before -->
                    <td class="pa-3 border-r text-right grey--text">
                      {{ diff.beforeDebit > 0 ? formatCurrencyWithSymbol(diff.beforeDebit) : '-' }}
                    </td>
                    <td class="pa-3 border-r text-right grey--text">
                      {{ diff.beforeCredit > 0 ? formatCurrencyWithSymbol(diff.beforeCredit) : '-' }}
                    </td>
                    <!-- After -->
                    <td
                      class="pa-3 border-r text-right"
                      :class="
                        diff.debitChanged
                          ? 'blue--text font-weight-black'
                          : diff.afterDebit > 0
                          ? 'font-weight-bold'
                          : 'grey--text'
                      "
                    >
                      {{ diff.afterDebit > 0 ? formatCurrencyWithSymbol(diff.afterDebit) : '-' }}
                    </td>
                    <td
                      class="pa-3 text-right"
                      :class="
                        diff.creditChanged
                          ? 'blue--text font-weight-black'
                          : diff.afterCredit > 0
                          ? 'font-weight-bold'
                          : 'grey--text'
                      "
                    >
                      {{ diff.afterCredit > 0 ? formatCurrencyWithSymbol(diff.afterCredit) : '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- COMPARISON FOR ACCOUNT -->
          <div v-else-if="infoDialog.item.entityType === 'ACCOUNT'">
            <h4
              class="text-subtitle-2 font-weight-black grey--text text--darken-4 uppercase tracking-wider mb-3 px-2"
            >
              Account Comparison (Before vs After)
            </h4>
            <div class="border rounded-lg overflow-hidden">
              <table class="w-full text-xs">
                <thead class="bg-gray-100 text-gray-700">
                  <tr>
                    <th class="text-left py-2 px-3 font-semibold" width="25%">Field</th>
                    <th class="text-left py-2 px-3 font-semibold border-l" width="35%">Before Value (Snapshot)</th>
                    <th class="text-left py-2 px-3 font-semibold border-l" width="40%">After Value (Requested)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(diff, idx) in getAccountDiffList(infoDialog.item)"
                    :key="`diff-${idx}`"
                    class="border-t hover:bg-gray-50"
                  >
                    <td class="py-2 px-3 font-semibold text-gray-700">{{ diff.label }}</td>
                    <td class="py-2 px-3 text-gray-600 border-l">
                      <span v-if="diff.oldValue">{{ diff.oldValue }}</span>
                      <span v-else class="text-gray-400 italic">None</span>
                    </td>
                    <td class="py-2 px-3 text-blue-700 border-l font-semibold">
                      {{ diff.newValue }}
                    </td>
                  </tr>
                  <tr v-if="getAccountDiffList(infoDialog.item).length === 0">
                    <td colspan="3" class="py-4 text-center text-gray-500 italic">
                      {{
                        infoDialog.item.approvalType.includes('CREATE')
                          ? 'New Account Creation Request'
                          : 'No field differences detected.'
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Reason / Remarks -->
          <div v-if="infoDialog.item.remarks || infoDialog.item.payload?.remarks" class="mt-4 px-2">
            <div class="text-[10px] font-weight-bold grey--text uppercase tracking-wider mb-1">
              Reason / Remarks
            </div>
            <div class="text-xs bg-slate-50 border rounded p-2 text-gray-700 italic">
              "{{ infoDialog.item.remarks || infoDialog.item.payload?.remarks }}"
            </div>
          </div>

          <!-- Approver Audit Trail / History -->
          <div
            v-if="infoDialog.item.approvers && infoDialog.item.approvers.length > 0"
            class="mt-4 px-2"
          >
            <div class="text-[10px] font-weight-bold grey--text uppercase tracking-wider mb-2">
              Workflow Approvers Progress
            </div>
            <div class="d-flex flex-wrap gap-2">
              <div
                v-for="(approver, aIdx) in infoDialog.item.approvers"
                :key="`approver-${aIdx}`"
                class="pa-2 px-3 rounded border bg-white d-flex align-center gap-2"
              >
                <v-icon size="14" color="primary">mdi-account-outline</v-icon>
                <span class="text-xs font-weight-bold">{{ getApproverUserLabel(approver) }}</span>
                <v-chip
                  x-small
                  label
                  class="font-weight-black uppercase"
                  :color="
                    approver.userAction === 'APPROVED'
                      ? 'green lighten-4 text-green-900'
                      : approver.userAction === 'REJECTED'
                      ? 'red lighten-4 text-red-900'
                      : 'amber lighten-4 text-amber-900'
                  "
                >
                  {{ approver.userAction || approver.status || 'PENDING' }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Actions Footer -->
        <v-divider></v-divider>
        <v-card-actions class="px-4 py-3 bg-gray-50 d-flex justify-space-between align-center">
          <div>
            <span class="text-xs text-gray-500">
              Status:
              <strong :class="getStatusTextColor(infoDialog.item.status)">{{
                infoDialog.item.status
              }}</strong>
            </span>
          </div>

          <div class="d-flex align-center">
            <!-- Inline Approve / Reject Buttons if Pending -->
            <template v-if="infoDialog.item.status === 'PENDING'">
              <v-btn
                small
                color="error"
                outlined
                class="mr-2 text-none"
                @click="openActionFromInfo('REJECT')"
              >
                <v-icon small left>mdi-close</v-icon>
                Reject
              </v-btn>
              <v-btn
                small
                color="success"
                depressed
                class="mr-2 text-none"
                @click="openActionFromInfo('APPROVE')"
              >
                <v-icon small left>mdi-check</v-icon>
                Approve
              </v-btn>
            </template>

            <v-btn small depressed class="text-none" @click="infoDialog.show = false">
              Close
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'AccountApprovalTable',
  data() {
    return {
      approvals: [],
      loading: false,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      filters: {
        entityType: '',
        status: ''
      },
      headers: [
        { text: 'Info', value: 'viewDetails', sortable: false, width: '60px' },
        { text: 'Entity / Reference', value: 'entityDetails', sortable: false, width: '500px' },
        { text: 'Requested By', value: 'requestedBy', sortable: false, width: '180px' },
        { text: 'Approval Type', value: 'approvalType', sortable: false, width: '150px' },
        { text: 'Remarks', value: 'remarks', sortable: false },
        { text: 'Status', value: 'status', sortable: false, width: '110px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '190px' }
      ],
      statusOptions: [
        { text: 'All', value: '' },
        { text: 'Pending', value: 'PENDING' },
        { text: 'Approved', value: 'APPROVED' },
        { text: 'Rejected', value: 'REJECTED' }
      ],
      dialog: {
        show: false,
        actionType: 'APPROVE',
        item: null,
        rejectionReason: '',
        remarks: '',
        submitting: false
      },
      infoDialog: {
        show: false,
        item: null
      }
    }
  },
  mounted() {
    this.fetchApprovals()
  },
  watch: {
    options: {
      handler() {
        this.fetchApprovals()
      },
      deep: true
    }
  },
  methods: {
    handleFilterChange() {
      this.options.page = 1
      this.fetchApprovals()
    },
    async fetchApprovals() {
      this.loading = true
      try {
        const { page = 1, itemsPerPage = 10 } = this.options || {}
        const params = {
          page,
          limit: itemsPerPage,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          entityType: this.filters.entityType || undefined,
          status: this.filters.status || undefined
        }
        const response = await this.$axios.get('/orgSetup/account-management-approvals', { params })
        this.approvals = response.data?.approvals || []
        this.totalItems = response.data?.pagination?.totalCount || this.approvals.length || 0
      } catch (error) {
        console.error('Error fetching account approvals:', error)
      } finally {
        this.loading = false
      }
    },
    getEntityIdentifier(item) {
      if (!item) return 'N/A'
      if (item.entityType === 'ACCOUNT') {
        return item.accountDetails?.name || item.payload?.name || item.snapshot?.name || 'Account'
      } else if (item.entityType === 'JOURNAL') {
        return (
          item.journalDetails?.voucherNo ||
          item.payload?.voucherNo ||
          item.snapshot?.voucherNo ||
          'Journal'
        )
      }
      return item.entityType || 'N/A'
    },
    getEntitySecondaryInfo(item) {
      if (!item) return ''
      if (item.entityType === 'ACCOUNT') {
        const code = item.accountDetails?.code || item.payload?.code || item.snapshot?.code
        const type =
          item.accountDetails?.accountType ||
          item.payload?.accountType ||
          item.payload?.category ||
          item.snapshot?.accountType ||
          item.snapshot?.category
        return [code ? `Code: ${code}` : '', type ? `Type: ${type}` : '']
          .filter(Boolean)
          .join(' • ')
      } else if (item.entityType === 'JOURNAL') {
        const amount = this.getJournalTotalAmount(item)
        const date = item.payload?.date || item.snapshot?.date || item.journalDetails?.date
        return [amount ? `₹${this.formatCurrency(amount)}` : '', date ? this.formatDate(date) : '']
          .filter(Boolean)
          .join(' • ')
      }
      return ''
    },
    getRequesterName(item) {
      if (!item) return 'Unknown'
      const req =
        item.requesterDetails || (typeof item.requestedBy === 'object' ? item.requestedBy : null)
      if (req) {
        return `${req.firstName || ''} ${req.lastName || ''}`.trim() || req.email || 'Requester'
      }
      return 'Unknown'
    },
    getRejectionReason(item) {
      const approverWithRejection = item?.approvers?.find?.(
        (a) => a.userAction === 'REJECTED' || a.rejectionReason
      )
      return approverWithRejection?.rejectionReason || 'Rejected by reviewer'
    },
    getApproverRemarks(item) {
      const approverWithRemarks = item?.approvers?.find?.((a) => a.remarks)
      return approverWithRemarks?.remarks || ''
    },
    getApprovedDate(item) {
      const approved = item?.approvers?.find?.((a) => a.userAction === 'APPROVED' && a.approvedOn)
      return approved?.approvedOn || item?.updatedAt || null
    },
    getApproverUserLabel(approver) {
      if (!approver) return 'Reviewer'
      const u = approver.userId
      if (typeof u === 'object' && u !== null) {
        return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Reviewer'
      }
      return `User (${approver.userId || 'Reviewer'})`
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return this.$dayjs
        ? this.$dayjs(date).format('DD/MM/YYYY hh:mm A')
        : new Date(date).toLocaleDateString()
    },
    formatAuditDateTime(dateStr) {
      if (!dateStr) return '-'
      return this.$dayjs
        ? this.$dayjs(dateStr).format('DD MMM YYYY, hh:mm a')
        : new Date(dateStr).toLocaleString()
    },
    formatCurrencyWithSymbol(val) {
      return (
        '₹' +
        Number(val || 0).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      )
    },
    getApprovalActionColor(type) {
      if (!type) return 'primary'
      const t = String(type).toUpperCase()
      if (t.includes('CREATE') || t.includes('INSERT')) return 'green'
      if (t.includes('DELETE') || t.includes('REMOVE')) return 'red'
      if (t.includes('UPDATE') || t.includes('EDIT')) return 'primary'
      return 'primary'
    },
    getApprovalActionText(type) {
      if (!type) return 'UPDATE'
      const t = String(type).toUpperCase()
      if (t.includes('CREATE') || t.includes('INSERT')) return 'CREATE'
      if (t.includes('DELETE') || t.includes('REMOVE')) return 'DELETE'
      if (t.includes('UPDATE') || t.includes('EDIT')) return 'UPDATE'
      return type.replace(/_/g, ' ')
    },
    getApprovalVoucherNo(approval) {
      if (!approval) return ''
      return (
        approval.payload?.voucherNo ||
        approval.snapshot?.voucherNo ||
        approval.journalDetails?.voucherNo ||
        approval.voucherNo ||
        approval.accountDetails?.name ||
        approval._id ||
        ''
      )
    },
    getApprovalDialogTitle(approval) {
      if (!approval) return 'Approval Details Comparison'
      const action = this.getApprovalActionText(approval.approvalType) || 'Approval'
      const entity = approval.entityType === 'JOURNAL' ? 'Journal' : 'Account'
      const identifier = this.getApprovalVoucherNo(approval)
      if (identifier) {
        return `${entity} ${action} Comparison (${identifier})`
      }
      return `${entity} ${action} Comparison`
    },
    calculateApprovalComparison(approval) {
      if (!approval) return []
      const snapshot = approval.snapshot || {}
      const payload = approval.payload || {}

      if (
        approval.entityType === 'JOURNAL' ||
        payload.voucherNo ||
        snapshot.voucherNo ||
        payload.oppositeLegs
      ) {
        const rows = []
        const beforeLegs = snapshot.legs || snapshot.beforeLegs || []
        const newOppositeLegs = payload.oppositeLegs

        // When payload has oppositeLegs (standard multi-leg journal update)
        if (Array.isArray(newOppositeLegs) && newOppositeLegs.length > 0) {
          const leg0Opposite = newOppositeLegs[0]
          const primaryAccountName =
            snapshot.primaryAccountName ||
            (leg0Opposite && leg0Opposite.particulars ? leg0Opposite.particulars : null) ||
            snapshot.particulars ||
            approval.accountDetails?.name ||
            'Primary Account'

          newOppositeLegs.forEach((leg, idx) => {
            const oppName =
              leg.oppositeParticulars ||
              leg.particulars ||
              `Opposite Account #${idx + 1}`

            // Find matching before legs for this specific opposite leg
            let matchingBeforePrim = null
            let matchingBeforeOpp = null

            if (Array.isArray(beforeLegs) && beforeLegs.length > 0) {
              if (leg.primaryLegId) {
                matchingBeforePrim = beforeLegs.find(
                  (b) => String(b._id) === String(leg.primaryLegId)
                )
              }
              if (leg._id) {
                matchingBeforeOpp = beforeLegs.find(
                  (b) => String(b._id) === String(leg._id)
                )
              }

              // Match by account particulars if ID matching didn't find it
              if (!matchingBeforePrim && primaryAccountName) {
                matchingBeforePrim = beforeLegs.find(
                  (b) =>
                    (b.particulars && b.particulars.trim().toLowerCase() === oppName.trim().toLowerCase()) ||
                    (b.particulars && b.particulars.trim().toLowerCase() === primaryAccountName.trim().toLowerCase())
                )
              }
              if (!matchingBeforeOpp && oppName) {
                matchingBeforeOpp = beforeLegs.find(
                  (b) =>
                    b !== matchingBeforePrim &&
                    b.particulars &&
                    b.particulars.trim().toLowerCase() === oppName.trim().toLowerCase()
                )
              }

              // Fallback matching by index
              if (!matchingBeforePrim && beforeLegs.length > idx * 2) {
                matchingBeforePrim = beforeLegs[idx * 2]
              }
              if (!matchingBeforeOpp && beforeLegs.length > idx * 2 + 1) {
                matchingBeforeOpp = beforeLegs[idx * 2 + 1]
              }
            } else if (snapshot && (snapshot.debit !== undefined || snapshot.credit !== undefined)) {
              // Legacy single-leg snapshot: snapshot IS the primary leg (or single leg)
              if (idx === 0) {
                matchingBeforePrim = snapshot
                // Infer opposite leg from snapshot
                matchingBeforeOpp = {
                  debit: snapshot.credit || 0,
                  credit: snapshot.debit || 0,
                  description: snapshot.description || ''
                }
              }
            }

            // After values
            const afterPrimDebit = Number(
              leg.primaryDebit !== undefined ? leg.primaryDebit : leg.credit || 0
            )
            const afterPrimCredit = Number(
              leg.primaryCredit !== undefined ? leg.primaryCredit : leg.debit || 0
            )
            const afterOppDebit = Number(leg.debit || 0)
            const afterOppCredit = Number(leg.credit || 0)

            // Before values
            const beforePrimDebit = Number(matchingBeforePrim?.debit || 0)
            const beforePrimCredit = Number(matchingBeforePrim?.credit || 0)
            const beforeOppDebit = Number(matchingBeforeOpp?.debit || 0)
            const beforeOppCredit = Number(matchingBeforeOpp?.credit || 0)

            // Primary row for this leg
            const primDescBefore = matchingBeforePrim?.description || snapshot.description || ''
            const primDescAfter = leg.description || payload.notes || snapshot.description || ''
            rows.push({
              particulars: `${primaryAccountName} (Leg #${idx + 1})`,
              role: 'Primary Account',
              beforeDebit: beforePrimDebit,
              beforeCredit: beforePrimCredit,
              afterDebit: afterPrimDebit,
              afterCredit: afterPrimCredit,
              descBefore: primDescBefore,
              descAfter: primDescAfter,
              debitChanged: beforePrimDebit !== afterPrimDebit,
              creditChanged: beforePrimCredit !== afterPrimCredit,
              descChanged: Boolean(primDescBefore && primDescAfter && primDescBefore !== primDescAfter)
            })

            // Opposite row for this leg
            const oppDescBefore = matchingBeforeOpp?.description || ''
            const oppDescAfter = leg.description || payload.notes || ''
            rows.push({
              particulars: oppName,
              role: 'Opposite Account',
              beforeDebit: beforeOppDebit,
              beforeCredit: beforeOppCredit,
              afterDebit: afterOppDebit,
              afterCredit: afterOppCredit,
              descBefore: oppDescBefore,
              descAfter: oppDescAfter,
              debitChanged: beforeOppDebit !== afterOppDebit,
              creditChanged: beforeOppCredit !== afterOppCredit,
              descChanged: Boolean(oppDescBefore && oppDescAfter && oppDescBefore !== oppDescAfter)
            })
          })

          return rows
        }

        // Fallback for generic leg mappings
        const beforeList = []
        const afterList = []

        if (Array.isArray(beforeLegs) && beforeLegs.length > 0) {
          beforeLegs.forEach((leg) => {
            beforeList.push({
              id: leg._id || leg.accountsManagementId,
              particulars: leg.particulars || 'Account',
              debit: Number(leg.debit || 0),
              credit: Number(leg.credit || 0),
              description: leg.description || ''
            })
          })
        }

        if (Array.isArray(payload.items)) {
          payload.items.forEach((item) => {
            afterList.push({
              id: item._id || item.accountsManagementId,
              particulars: item.accountName || item.particulars || 'Account',
              debit: Number(item.debit || 0),
              credit: Number(item.credit || 0),
              description: item.description || ''
            })
          })
        }

        const map = {}
        const orderedKeys = []

        beforeList.forEach((leg) => {
          const key = leg.particulars || leg.id
          if (!map[key]) {
            map[key] = {
              particulars: leg.particulars,
              beforeDebit: leg.debit,
              beforeCredit: leg.credit,
              afterDebit: 0,
              afterCredit: 0,
              descBefore: leg.description,
              descAfter: ''
            }
            orderedKeys.push(key)
          } else {
            map[key].beforeDebit += leg.debit
            map[key].beforeCredit += leg.credit
          }
        })

        afterList.forEach((leg) => {
          const key = leg.particulars || leg.id
          if (!map[key]) {
            map[key] = {
              particulars: leg.particulars,
              beforeDebit: 0,
              beforeCredit: 0,
              afterDebit: leg.debit,
              afterCredit: leg.credit,
              descBefore: '',
              descAfter: leg.description
            }
            orderedKeys.push(key)
          } else {
            map[key].afterDebit += leg.debit
            map[key].afterCredit += leg.credit
          }
        })

        return orderedKeys.map((key) => {
          const item = map[key]
          item.debitChanged = item.beforeDebit !== item.afterDebit
          item.creditChanged = item.beforeCredit !== item.afterCredit
          item.descChanged = Boolean(item.descBefore && item.descAfter && item.descBefore !== item.descAfter)
          return item
        })
      }
      return []
    },
    formatCurrency(val) {
      if (val === null || val === undefined || val === '') return '0.00'
      const num = Number(val)
      if (isNaN(num)) return val
      return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatApprovalType(type) {
      if (!type) return 'N/A'
      return type.replace(/_/g, ' ')
    },
    isUpdateAction(type) {
      if (!type) return false
      return type.includes('UPDATE') || type.includes('EDIT')
    },
    getApprovalTypeColor(type) {
      if (!type) return 'grey lighten-4 text-gray-700'
      if (type.includes('DELETE')) return 'red lighten-5 text-red-700'
      if (type.includes('UPDATE') || type.includes('EDIT')) return 'amber lighten-5 text-amber-800'
      if (type.includes('CREATE')) return 'green lighten-5 text-green-700'
      return 'blue lighten-5 text-blue-700'
    },
    getStatusColor(status) {
      switch (status) {
        case 'APPROVED':
          return 'green lighten-5 text-green-700'
        case 'REJECTED':
          return 'red lighten-5 text-red-700'
        case 'PENDING':
          return 'orange lighten-5 text-orange-700'
        default:
          return 'grey lighten-4 text-gray-700'
      }
    },
    getStatusTextColor(status) {
      switch (status) {
        case 'APPROVED':
          return 'text-green-600'
        case 'REJECTED':
          return 'text-red-600'
        case 'PENDING':
          return 'text-amber-600'
        default:
          return 'text-gray-600'
      }
    },
    getAccountFieldValue(item, field) {
      if (!item) return ''
      return item.payload?.[field] ?? item.snapshot?.[field] ?? item.accountDetails?.[field] ?? ''
    },
    getFormattedAddress(addr) {
      if (!addr) return ''
      if (typeof addr === 'string') return addr
      const parts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.country,
        addr.pincode
      ].filter(Boolean)
      return parts.join(', ')
    },
    getAccountDiffList(item) {
      if (!item) return []
      const payload = item.payload || {}
      const snapshot = item.snapshot || {}
      const fields = [
        { key: 'name', label: 'Account Name' },
        { key: 'code', label: 'Account Code' },
        { key: 'category', label: 'Category / Type' },
        { key: 'accountType', label: 'Account Type' },
        { key: 'currency', label: 'Currency' },
        { key: 'openingBalance', label: 'Opening Balance', isCurrency: true },
        { key: 'mailingName', label: 'Mailing Name' },
        { key: 'contactPerson', label: 'Contact Person' },
        { key: 'mobileNo', label: 'Mobile Number' },
        { key: 'phoneNo', label: 'Phone Number' },
        { key: 'email', label: 'Email' },
        { key: 'panItNo', label: 'PAN / IT No' },
        { key: 'description', label: 'Description' }
      ]

      const diffs = []
      fields.forEach((f) => {
        const newVal = payload[f.key]
        const oldVal = snapshot[f.key]
        if (newVal !== undefined && newVal !== oldVal) {
          diffs.push({
            label: f.label,
            oldValue: f.isCurrency
              ? oldVal !== undefined
                ? `₹${this.formatCurrency(oldVal)}`
                : ''
              : String(oldVal || ''),
            newValue: f.isCurrency ? `₹${this.formatCurrency(newVal)}` : String(newVal || '')
          })
        }
      })
      return diffs
    },
    getJournalFieldValue(item, field) {
      if (!item) return ''
      return item.payload?.[field] ?? item.snapshot?.[field] ?? item.journalDetails?.[field] ?? ''
    },
    getJournalTotalAmount(item) {
      if (!item) return 0
      const p = item.payload || {}
      const s = item.snapshot || {}
      const j = item.journalDetails || {}
      return (
        p.totalAmount ??
        p.amount ??
        p.totalDebit ??
        s.totalAmount ??
        s.amount ??
        j.debit ??
        j.credit ??
        0
      )
    },
    getJournalLegs(item) {
      if (!item) return []
      if (Array.isArray(item.payload?.oppositeLegs) && item.payload.oppositeLegs.length > 0) {
        return item.payload.oppositeLegs
      }
      if (Array.isArray(item.snapshot?.oppositeLegs) && item.snapshot.oppositeLegs.length > 0) {
        return item.snapshot.oppositeLegs
      }
      return []
    },
    getJournalLegsTotal(item, type) {
      const legs = this.getJournalLegs(item)
      return legs.reduce((sum, leg) => sum + Number(leg[type] || 0), 0)
    },
    isJournalBalanced(item) {
      const debitTotal = this.getJournalLegsTotal(item, 'debit')
      const creditTotal = this.getJournalLegsTotal(item, 'credit')
      return Math.abs(debitTotal - creditTotal) < 0.01
    },
    openInfoDialog(item) {
      this.infoDialog.item = item
      this.infoDialog.show = true
    },
    openActionFromInfo(actionType) {
      const item = this.infoDialog.item
      this.infoDialog.show = false
      this.openActionDialog(item, actionType)
    },
    openActionDialog(item, actionType) {
      this.dialog.item = item
      this.dialog.actionType = actionType
      this.dialog.rejectionReason = ''
      this.dialog.remarks = ''
      this.dialog.submitting = false
      this.dialog.show = true
    },
    async submitAction() {
      if (!this.dialog.item) return
      this.dialog.submitting = true
      try {
        const id = this.dialog.item._id
        const isApprove = this.dialog.actionType === 'APPROVE'
        const endpoint = `/orgSetup/account-management-approvals/${id}/${
          isApprove ? 'approve' : 'reject'
        }`
        const payload = isApprove
          ? { remarks: this.dialog.remarks }
          : {
              rejectionReason: this.dialog.rejectionReason,
              remarks: this.dialog.remarks
            }

        await this.$axios.post(endpoint, payload)
        this.$toast.success(`Request ${isApprove ? 'approved' : 'rejected'} successfully!`)
        this.dialog.show = false
        await this.fetchApprovals()
      } catch (error) {
        console.error('Error processing approval action:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to process request.')
      } finally {
        this.dialog.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.account-approval-table-container {
  border-radius: 8px;
  overflow: hidden;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.info-dialog-card {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  padding:10px
}
.overview-banner {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-color: #e2e8f0;
}
.diff-table th,
.diff-table td {
  vertical-align: middle;
}
.legs-table th,
.legs-table td {
  vertical-align: middle;
}
.py-1-5 {
  padding-top: 6px;
  padding-bottom: 6px;
}
.p-2-5 {
  padding: 10px;
}
.text-xxs {
  font-size: 11px;
}
.gap-2 {
  gap: 8px;
}
.space-y-2 > * + * {
  margin-top: 8px;
}
.w-max {
  width: max-content;
}
</style>
